import React from 'react';
import produce from 'immer';
import { MetricEntry } from 'common/query-builder/query-entry-section/types';
import { ChangeEvent } from 'common/components/select/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { InputReact } from 'common/components/input';
import { SelectReact } from 'common/components/select';
import { NAVIGATION_KEY_CODES } from 'common/util/dom';
import { SectionItem } from 'common/components/items-menu/types';
import { ConversionWindowUnit, FUNNELS_MATH_TYPES } from 'common/types/reports/bookmark';
import { useStagedState } from 'common/hooks/useStagedState';
import { CONVERSION_WINDOW_SIZE_LIMITS, CONVERSION_WINDOW_UNITS } from 'common/report/constants';
import { useTranslation } from 'common/i18n/hooks';
import type { RowProps } from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import styles from './index.module.styl';
export type ConversionCriteriaProps = {
  entry: MetricEntry;
  onError: (error: string | undefined) => void;
  updateEntry: RowProps[`updateEntry`];
};
export function ConversionCriteria(props: ConversionCriteriaProps) {
  var _props$entry$measurem;
  const {
    t
  } = useTranslation();

  // Conversion criteria cannot be changed if the count type is by session
  // because then the conversion window *must* be set to 1 session.
  const shouldBeDisabled = [FUNNELS_MATH_TYPES.SESSION, FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION].includes(((_props$entry$measurem = props.entry.measurement) === null || _props$entry$measurem === void 0 ? void 0 : _props$entry$measurem.aggregationOperator) as FUNNELS_MATH_TYPES);
  const handleConversionWindowUpdate = (opts: {
    updatedValue?: number;
    updatedUnit?: ConversionWindowUnit;
  }) => {
    const updatedEntry = produce(props.entry, entry => {
      if (opts.updatedValue) {
        entry.behavior.conversionWindowDuration = opts.updatedValue;
      }
      if (opts.updatedUnit) {
        entry.behavior.conversionWindowUnit = opts.updatedUnit;
      }
      entry.hasUnsavedChanges = true;
      return entry;
    });
    props.updateEntry(updatedEntry);
  };
  const [conversionWindowErrorMessage, setConversionWindowErrorMessage] = React.useState<string | undefined>(undefined);
  const [selectedUnit, setSelectedUnit] = useStagedState(props.entry.behavior.conversionWindowUnit || ConversionWindowUnit.Day, state => {
    handleConversionWindowUpdate({
      updatedUnit: state
    });
  });
  const [selectedValue, setSelectedValue] = useStagedState(props.entry.behavior.conversionWindowDuration || 7, state => {
    handleConversionWindowUpdate({
      updatedValue: state
    });
  });

  /**
   * Ensure the conversion window inputs are valid.
   * @param value - conversion window length (e.g. 7)
   * @param unit - conversion window unit (e.g. day/week)
   * @return error message if conversion window input is invalid, null otherwise
   */
  const validateConversionWindow = (value: number, unit: ConversionWindowUnit): string | undefined => {
    let error = undefined;
    if (Number.isNaN(value)) {
      error = "Input must be an integer";
    } else if (value < CONVERSION_WINDOW_SIZE_LIMITS[unit].MIN) {
      error = "Conversion window below ".concat(CONVERSION_WINDOW_SIZE_LIMITS[unit].MIN, " ").concat(unit, " minimum");
    } else if (value > CONVERSION_WINDOW_SIZE_LIMITS[unit].MAX) {
      error = "Conversion window exceeds ".concat(CONVERSION_WINDOW_SIZE_LIMITS[unit].MAX, " ").concat(unit, " maximum");
    }
    return error;
  };
  const updateConversionWindowErrorMessage = (errorMessage: string | undefined) => {
    setConversionWindowErrorMessage(errorMessage);
    props.onError(errorMessage);
  };
  const handleConversionWindowUnitChange = (item: SectionItem<ConversionWindowUnit>) => {
    const newUnit = item.value;
    if (!item.isSelected && newUnit) {
      const value = selectedValue;
      const errorMessage = validateConversionWindow(value, newUnit);
      updateConversionWindowErrorMessage(errorMessage);
      setSelectedUnit(newUnit);
      if (!errorMessage) {
        handleConversionWindowUpdate({
          updatedUnit: newUnit
        });
      }
    }
  };
  const handleConversionWindowValueChange = (newValueRaw: string) => {
    const newValue = parseInt(newValueRaw, 10);
    const newErrorMessage = validateConversionWindow(newValue, selectedUnit);
    updateConversionWindowErrorMessage(newErrorMessage);
    setSelectedValue(newValue);
  };
  const handleKeyStroke = (keyCode: number) => {
    if (keyCode === NAVIGATION_KEY_CODES.ENTER && !conversionWindowErrorMessage) {
      handleConversionWindowUpdate({
        updatedValue: selectedValue,
        updatedUnit: selectedUnit
      });
    }
  };
  return <>
      <div className={styles.iconWrapper}>
        <SvgIconReact icon='time' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      </div>

      <span className={styles.withinLabel}>{t("query-builder.conversion.within")}</span>

      {/** Funnel conversion window unit amount. E.g. select X in "X days"*/}
      <InputReact className={styles.conversionWindowInput} disabled={shouldBeDisabled} role='input' type='number' size='medium' value={"".concat(selectedValue)} validationStatus={conversionWindowErrorMessage ? "invalid" : ""} onInput={ev => handleConversionWindowValueChange(ev.target.value)} onBlur={() => handleConversionWindowUpdate({
      updatedValue: selectedValue,
      updatedUnit: selectedUnit
    })} onKeyup={ev => handleKeyStroke(ev.keyCode)} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />

      {/* Funnel conversion unit selection, on each day, week, month, etc... */}
      <SelectReact disabled={shouldBeDisabled} sections={(() => {
      const labels: Record<ConversionWindowUnit, (count: number) => string> = {
        second: count => t("reports.time-unit.second", {
          count
        }),
        minute: count => t("reports.time-unit.minute", {
          count
        }),
        hour: count => t("reports.time-unit.hour", {
          count
        }),
        day: count => t("reports.time-unit.day", {
          count
        }),
        week: count => t("reports.time-unit.week", {
          count
        }),
        month: count => t("reports.time-unit.month", {
          count
        }),
        session: count => t("reports.time-unit.session", {
          count
        })
      };
      return [{
        items: CONVERSION_WINDOW_UNITS.map(unit => ({
          value: unit,
          label: labels[unit] ? labels[unit](selectedValue).toLowerCase() : unit,
          isSelected: unit === selectedUnit
        }))
      }];
    })()} onChange={ev => handleConversionWindowUnitChange((ev as ChangeEvent).detail.item)} menuWidth='-1' data-sentry-element="SelectReact" data-sentry-source-file="index.tsx" />
    </>;
}